import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import { TTranslateFunction } from '@modules/localization/types/TranslateFunction';
import { ReferenceType } from '@modules/client/types/globalTypes';
import TreeNode from '../interfaces/TreeNode';
import { TTreeListData } from '../SideBarContentContext';
import { getTranslatedNodeIdForReferenceType } from '../../../apiReference/page/utils/sidebar';

export const DEBOUNCE_DELAY_MILLISECONDS = process.env.NODE_ENV === 'development' ? 200 : 100;

const filterNodes = (
  node: TreeNode,
  filterQuery: string,
  expandedIds: string[],
  translate: TTranslateFunction,
): TreeNode | null => {
  if (!filterQuery) {
    return node;
  }

  const nodeMatches = node.value.toLowerCase().includes(filterQuery);
  if (node.id === getTranslatedNodeIdForReferenceType(ReferenceType.CLASS, translate)) {
    const collectMatchingChildren = (currentNode: TreeNode): TreeNode[] => {
      if (!currentNode.children) return [];
      return currentNode.children.reduce((acc: TreeNode[], child) => {
        if (child.value.toLowerCase().includes(filterQuery)) {
          acc.push({ ...child, children: null });
        }
        return [...acc, ...collectMatchingChildren(child)];
      }, []);
    };

    const matchingChildren = collectMatchingChildren(node);
    if (nodeMatches || matchingChildren.length > 0) {
      expandedIds.push(node.id);
      const sortedChildren = matchingChildren.sort((a, b) =>
        a.value.toLowerCase().localeCompare(b.value.toLowerCase()),
      );
      return { ...node, children: sortedChildren };
    }
    return null;
  }

  const children = node.children
    ?.map((child) => filterNodes(child, filterQuery, expandedIds, translate))
    .filter((child): child is TreeNode => child !== null);

  if (nodeMatches || (children && children.length > 0)) {
    if (children && children.length > 0) {
      expandedIds.push(node.id);
    }
    return {
      ...node,
      children: children || null,
    };
  }

  return null;
};

export const useSidebarFilterQuery = (onNewFilterQuery?: () => void) => {
  const [filterQuery, setFilterQuery] = useState('');
  const [debouncedFilterQuery, setDebouncedFilterQuery] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const lastProcessedQueryRef = useRef('');
  const filterQueryLower = useMemo(() => filterQuery.toLowerCase().trim(), [filterQuery]);

  const resetFilterQuery = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setFilterQuery('');
    setDebouncedFilterQuery('');
    lastProcessedQueryRef.current = '';
    onNewFilterQuery?.();
  }, [onNewFilterQuery]);

  useEffect(() => {
    if (filterQueryLower === lastProcessedQueryRef.current) {
      return;
    }

    if (
      filterQueryLower &&
      lastProcessedQueryRef.current &&
      !filterQueryLower.startsWith(lastProcessedQueryRef.current)
    ) {
      onNewFilterQuery?.();
    }

    if (filterQueryLower === '' && lastProcessedQueryRef.current !== '') {
      onNewFilterQuery?.();
    }

    lastProcessedQueryRef.current = filterQueryLower;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilterQuery(filterQueryLower);
    }, DEBOUNCE_DELAY_MILLISECONDS);

    // eslint-disable-next-line consistent-return
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filterQueryLower, onNewFilterQuery]);

  const hasFilterQuery = useMemo(() => debouncedFilterQuery.length > 0, [debouncedFilterQuery]);

  return {
    filterQuery,
    setFilterQuery,
    debouncedFilterQuery,
    filterQueryLower,
    resetFilterQuery,
    hasFilterQuery,
  };
};

export const useFilteredTreeData = (
  treeListData: TTreeListData,
  debouncedFilterQuery: string,
  setExpanded: (ids: string[]) => void,
) => {
  const { translate } = useTranslation();
  const cacheRef = useRef<
    Map<
      string,
      {
        data: TTreeListData;
        expandedIds: string[];
      }
    >
  >(new Map());
  const lastProcessedFilterQueryRef = useRef('');

  const filteredData = useMemo(() => {
    if (!debouncedFilterQuery) {
      return { data: treeListData, expandedIds: [] };
    }

    const cachedFilteredResult = cacheRef.current.get(debouncedFilterQuery);
    if (cachedFilteredResult) {
      return cachedFilteredResult;
    }

    const expandedNodeIds: string[] = [];
    const filteredTreeSections = treeListData
      .map(({ heading, nodes }) => ({
        heading,
        nodes: nodes
          .map((node) => filterNodes(node, debouncedFilterQuery, expandedNodeIds, translate))
          .filter((node): node is TreeNode => node !== null),
      }))
      .filter(({ nodes }) => nodes.length > 0);

    const filteredResult = {
      data: filteredTreeSections,
      expandedIds: expandedNodeIds,
    };
    cacheRef.current.set(debouncedFilterQuery, filteredResult);
    return filteredResult;
  }, [treeListData, debouncedFilterQuery, translate]);

  useEffect(() => {
    if (debouncedFilterQuery !== lastProcessedFilterQueryRef.current) {
      lastProcessedFilterQueryRef.current = debouncedFilterQuery;
      // NOTE (tchu, 2025-06-24): If the filter query is empty,
      // don't reset the expanded ids per jleonard's request
      if (debouncedFilterQuery !== '') {
        setExpanded(filteredData.expandedIds);
      }
    }
  }, [debouncedFilterQuery, filteredData.expandedIds, setExpanded, lastProcessedFilterQueryRef]);

  return filteredData;
};
